import React from "react";
import { Bot, RefreshCw, Search, Sparkles, Zap } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select";
import { AIModelType } from "@/services/aiServices/types";
import { fetchOpenRouterModels } from "@/services/aiServices/openrouterService";
import { ModelGroup, modelGroups } from "./models";

interface ModelSelectorProps {
  selectedModel: string;
  onModelChange: (model: AIModelType) => void;
  onRegenerate?: () => void;
  onClearCache?: () => void;
  loading?: boolean;
  disabled?: boolean;
  type: 'chat' | 'interview' | 'knowledge';
  groups?: ModelGroup[]; // Optional prop to override default model groups
}

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  selectedModel = '',
  onModelChange,
  groups = modelGroups,
  ...props
}) => {
  const { t } = useTranslation();
  const [remoteGroups, setRemoteGroups] = React.useState<ModelGroup[]>(groups);
  const [modelSearch, setModelSearch] = React.useState('');
  const [modelsLoading, setModelsLoading] = React.useState(false);
  const [modelsError, setModelsError] = React.useState<string | null>(null);
  const knownModels = remoteGroups.flatMap(group => group.models.map(model => model.id));
  const isCustomModel = !!selectedModel && !knownModels.includes(selectedModel);
  const [customModelInput, setCustomModelInput] = React.useState(isCustomModel ? selectedModel : '');
  const [customSelected, setCustomSelected] = React.useState(false);
  const isKnownModel = knownModels.includes(selectedModel);
  const isCustomMode = customSelected || isCustomModel || customModelInput.length > 0;

  React.useEffect(() => {
    let active = true;

    const loadModels = async () => {
      setModelsLoading(true);
      setModelsError(null);
      try {
        const models = await fetchOpenRouterModels();
        if (!active) return;

        const grouped = models.reduce<Record<string, ModelGroup>>((acc, model) => {
          const provider = model.id.split('/')[0] || 'custom';
          const label = providerLabel(provider);
          if (!acc[label]) {
            acc[label] = { label, models: [] };
          }
          acc[label].models.push({
            id: model.id,
            name: model.name || model.id,
            badge: model.context_length ? {
              text: `${Math.round(model.context_length / 1000)}k`,
              color: 'text-muted-foreground'
            } : undefined,
          });
          return acc;
        }, {});

        setRemoteGroups(Object.values(grouped));
      } catch (error) {
        if (!active) return;
        console.error('Failed to load OpenRouter models:', error);
        setModelsError(t('models.errors.loadFailed'));
      } finally {
        if (active) {
          setModelsLoading(false);
        }
      }
    };

    loadModels();

    return () => {
      active = false;
    };
  }, [t]);

  React.useEffect(() => {
    if (isKnownModel) {
      setCustomModelInput('');
      setCustomSelected(false);
    }
  }, [isKnownModel, selectedModel]);

  const handleModelChange = (value: string) => {
    if (value === 'custom-openai-model') {
      setCustomSelected(true);
      setCustomModelInput(isCustomModel ? selectedModel : '');
      return;
    }

    setCustomSelected(false);
    setCustomModelInput('');
    onModelChange(value);
  };

  const handleCustomModelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const model = event.target.value.trim();
    setCustomModelInput(event.target.value);
    if (model) {
      onModelChange(model);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4 mt-2 px-1">
      <Select
        value={isCustomMode ? 'custom-openai-model' : selectedModel || undefined}
        onValueChange={handleModelChange}
        disabled={props.loading || modelsLoading}
      >
        <SelectTrigger className="w-[200px] bg-white dark:bg-gray-950">
          <SelectValue placeholder={modelsLoading ? t('models.loading') : t(`${props.type}.models.select`)} />
        </SelectTrigger>
        <SelectContent className="bg-white dark:bg-gray-950">
          <div className="p-2">
            <div className="flex items-center gap-2 rounded-md border px-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                value={modelSearch}
                onChange={(event) => setModelSearch(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
                placeholder={t('models.searchPlaceholder')}
                className="h-8 w-full bg-transparent text-sm outline-none"
              />
            </div>
          </div>
          {filterGroups(remoteGroups, modelSearch).map((group, groupIndex) => (
            <SelectGroup key={`group-${groupIndex}`}>
              <SelectLabel className="px-2 py-1.5 text-xs text-muted-foreground">
                {translateLabel(t, group.label)}
              </SelectLabel>
              {group.models.map((model) => (
                <SelectItem key={model.id} value={model.id}>
                  <div className="flex items-center gap-2">
                    {providerIcon(model.id)}
                    <span>{translateLabel(t, model.name)}</span>
                    {model.badge && (
                      <span className={`ml-auto text-xs ${model.badge.color}`}>
                        {translateLabel(t, model.badge.text)}
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
          <SelectGroup>
            <SelectLabel className="px-2 py-1.5 text-xs text-muted-foreground">
              {t('models.groups.custom')}
            </SelectLabel>
            <SelectItem value="custom-openai-model">
              <div className="flex items-center gap-2">
                <Bot className="w-4 h-4 text-slate-500" />
                <span>{t('models.custom')}</span>
              </div>
            </SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
      {isCustomMode && (
        <Input
          value={customModelInput}
          onChange={handleCustomModelChange}
          disabled={props.loading}
          placeholder={t('models.customPlaceholder')}
          className="h-10 w-[220px] bg-white dark:bg-gray-950"
        />
      )}
      <Button
        variant="outline"
        size="icon"
        onClick={props.onRegenerate}
        disabled={props.disabled || props.loading}
        className="h-10 w-10 px-3 py-2"
        title={t(`${props.type}.actions.regenerate`)}
      >
        <RefreshCw className={cn("h-4 w-4", props.loading && "animate-spin")} />
      </Button>
      {modelsError && (
        <p className="basis-full text-xs text-red-500">{modelsError}</p>
      )}
    </div>
  );
};

function filterGroups(groups: ModelGroup[], search: string): ModelGroup[] {
  const normalizedSearch = search.trim().toLowerCase();

  return groups
    .map(group => ({
      ...group,
      models: group.models
        .filter(model => !normalizedSearch || `${model.id} ${model.name}`.toLowerCase().includes(normalizedSearch))
        .slice(0, normalizedSearch ? 80 : 25),
    }))
    .filter(group => group.models.length > 0);
}

function providerLabel(provider: string): string {
  const labels: Record<string, string> = {
    openai: 'OpenAI',
    anthropic: 'Anthropic / Claude',
    google: 'Google / Gemini',
    mistralai: 'Mistral',
    perplexity: 'Perplexity',
    meta: 'Meta',
    qwen: 'Qwen',
    deepseek: 'DeepSeek',
    xai: 'xAI',
  };

  return labels[provider] ?? provider;
}

function providerIcon(modelId: string): React.ReactNode {
  const provider = modelId.split('/')[0];

  if (provider === 'openai') {
    return <Zap className="w-4 h-4 text-blue-500" />;
  }

  if (provider === 'google') {
    return <Sparkles className="w-4 h-4 text-green-500" />;
  }

  return <Bot className="w-4 h-4 text-slate-500" />;
}

function translateLabel(t: (key: string) => string, value: string): string {
  const translated = t(value);
  return translated === value ? value : translated;
}
